import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { clienteService } from '../../services/authService';
import './Registration.css';

const Registration = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    documentType: 'cpf', // cpf, oab, cnpj
    documentNumber: '',
    oabState: '',
    precatorios: [
      {
        number: '',
        value: '',
        date: '',
        state: ''
      }
    ]
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Mask functions
  const maskPhone = (value) => {
    // Remove non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply mask (00) 00000-0000
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 7) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    } else if (cleaned.length <= 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const maskCPF = (value) => {
    // Remove non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply mask 000.000.000-00
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
    } else if (cleaned.length <= 9) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
    } else if (cleaned.length <= 11) {
      return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
    }
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
  };

  const maskCNPJ = (value) => {
    // Remove non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply mask 00.000.000/0000-00
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 5) {
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
    } else if (cleaned.length <= 8) {
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
    } else if (cleaned.length <= 12) {
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
    } else if (cleaned.length <= 14) {
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
    }
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
  };

  const maskCurrency = (value) => {
    // Remove non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Convert to number and back to handle leading zeros
    const numValue = parseInt(cleaned) || 0;
    
    // Convert cents to reais
    const reais = (numValue / 100).toFixed(2);
    
    // Format with thousand separators
    const parts = reais.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    return `R$ ${parts.join(',')}`;
  };

  const unmaskCurrency = (value) => {
    // Remove currency symbol and convert to number
    return value.replace(/[^0-9,]/g, '').replace(',', '.');
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let maskedValue = value;

    // Apply masks based on field name
    if (name === 'phone') {
      maskedValue = maskPhone(value);
    } else if (name === 'documentNumber') {
      if (formData.documentType === 'cpf') {
        maskedValue = maskCPF(value);
      } else if (formData.documentType === 'cnpj') {
        maskedValue = maskCNPJ(value);
      }
      // OAB numbers don't have a standard mask
    }

    setFormData(prev => ({
      ...prev,
      [name]: maskedValue
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle precatorio changes
  const handlePrecatorioChange = (index, field, value) => {
    const updatedPrecatorios = [...formData.precatorios];
    
    // Apply currency mask for value field
    if (field === 'value') {
      updatedPrecatorios[index][field] = maskCurrency(value);
    } else {
      updatedPrecatorios[index][field] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      precatorios: updatedPrecatorios
    }));
  };

  // Add new precatorio
  const addPrecatorio = () => {
    setFormData(prev => ({
      ...prev,
      precatorios: [
        ...prev.precatorios,
        {
          number: '',
          value: '',
          date: '',
          state: ''
        }
      ]
    }));
  };

  // Remove precatorio
  const removePrecatorio = (index) => {
    if (formData.precatorios.length > 1) {
      const updatedPrecatorios = formData.precatorios.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        precatorios: updatedPrecatorios
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!formData.documentNumber.trim()) {
      if (formData.documentType === 'cpf') {
        newErrors.documentNumber = 'CPF é obrigatório';
      } else if (formData.documentType === 'oab') {
        newErrors.documentNumber = 'Número da OAB é obrigatório';
      } else {
        newErrors.documentNumber = 'CNPJ é obrigatório';
      }
    }

    if (formData.documentType === 'oab' && !formData.oabState) {
      newErrors.oabState = 'Estado da OAB é obrigatório';
    }

    // Validate precatorios
    formData.precatorios.forEach((precatorio, index) => {
      if (!precatorio.number.trim()) {
        newErrors[`precatorio_${index}_number`] = 'Número do precatório é obrigatório';
      }
      if (!precatorio.value.trim()) {
        newErrors[`precatorio_${index}_value`] = 'Valor é obrigatório';
      }
      if (!precatorio.date) {
        newErrors[`precatorio_${index}_date`] = 'Data é obrigatória';
      }
      if (!precatorio.state) {
        newErrors[`precatorio_${index}_state`] = 'Estado é obrigatório';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      setSubmitError('');
      
      try {
        // Prepare data for submission
        const dataToSubmit = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove mask
          documentType: formData.documentType,
          documentNumber: formData.documentNumber.replace(/\D/g, ''), // Remove mask
          precatorios: formData.precatorios.map(p => ({
            number: p.number,
            value: parseFloat(unmaskCurrency(p.value)) || 0,
            date: p.date,
            state: p.state
          }))
        };
        
        // Only include oabState if documentType is 'oab'
        if (formData.documentType === 'oab') {
          dataToSubmit.oabState = formData.oabState;
        }
        
        const result = await clienteService.register(dataToSubmit);
        
        if (result.success) {
          alert('Cadastro realizado com sucesso! Entraremos em contato em breve.');
          navigate('/');
        } else {
          setSubmitError(result.message || 'Erro ao realizar cadastro');
          if (result.errors) {
            const newErrors = {};
            result.errors.forEach(error => {
              newErrors[error.path] = error.msg;
            });
            setErrors(newErrors);
          }
        }
      } catch (error) {
        setSubmitError('Erro ao conectar com o servidor. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Brazilian states
  const states = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <div className="registration-page">
      <Header />
      
      <main className="main-content">
        <div className="container">
          <div className="registration-content">
            <h1 className="page-title">Cadastro de Precatórios</h1>
            <p className="page-description">
              Preencha o formulário abaixo com suas informações e os dados dos seus precatórios. 
              Nossa equipe entrará em contato para apresentar a melhor proposta.
            </p>
            
            <form className="registration-form" onSubmit={handleSubmit}>
              {submitError && (
                <div className="form-error-box">
                  {submitError}
                </div>
              )}
              
              {/* Personal Information Section */}
              <div className="form-section">
                <h2 className="section-subtitle">Informações Pessoais</h2>
                
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nome Completo *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <span className="form-error">{errors.name}</span>}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">E-mail *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Telefone/Celular *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-input"
                      placeholder="(00) 00000-0000"
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength="15"
                    />
                    {errors.phone && <span className="form-error">{errors.phone}</span>}
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Tipo de Documento *</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="documentType"
                        value="cpf"
                        checked={formData.documentType === 'cpf'}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Clear document number when changing type
                          setFormData(prev => ({ ...prev, documentNumber: '' }));
                        }}
                      />
                      CPF (Pessoa Física)
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="documentType"
                        value="oab"
                        checked={formData.documentType === 'oab'}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Clear document number when changing type
                          setFormData(prev => ({ ...prev, documentNumber: '' }));
                        }}
                      />
                      OAB (Advogado)
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="documentType"
                        value="cnpj"
                        checked={formData.documentType === 'cnpj'}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Clear document number when changing type
                          setFormData(prev => ({ ...prev, documentNumber: '' }));
                        }}
                      />
                      CNPJ (Empresa)
                    </label>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="documentNumber" className="form-label">
                      {formData.documentType === 'cpf' && 'CPF *'}
                      {formData.documentType === 'oab' && 'Número da OAB *'}
                      {formData.documentType === 'cnpj' && 'CNPJ *'}
                    </label>
                    <input
                      type="text"
                      id="documentNumber"
                      name="documentNumber"
                      className="form-input"
                      placeholder={
                        formData.documentType === 'cpf' ? '000.000.000-00' :
                        formData.documentType === 'cnpj' ? '00.000.000/0000-00' :
                        'Número da OAB'
                      }
                      value={formData.documentNumber}
                      onChange={handleInputChange}
                      maxLength={
                        formData.documentType === 'cpf' ? 14 :
                        formData.documentType === 'cnpj' ? 18 :
                        20
                      }
                    />
                    {errors.documentNumber && <span className="form-error">{errors.documentNumber}</span>}
                  </div>
                  
                  {formData.documentType === 'oab' && (
                    <div className="form-group">
                      <label htmlFor="oabState" className="form-label">Estado da OAB *</label>
                      <select
                        id="oabState"
                        name="oabState"
                        className="form-select"
                        value={formData.oabState}
                        onChange={handleInputChange}
                      >
                        <option value="">Selecione</option>
                        {states.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      {errors.oabState && <span className="form-error">{errors.oabState}</span>}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Precatorios Section */}
              <div className="form-section">
                <h2 className="section-subtitle">Dados dos Precatórios</h2>
                
                {formData.precatorios.map((precatorio, index) => (
                  <div key={index} className="precatorio-item">
                    <div className="precatorio-header">
                      <h3>Precatório {index + 1}</h3>
                      {formData.precatorios.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removePrecatorio(index)}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Número do Precatório *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={precatorio.number}
                          onChange={(e) => handlePrecatorioChange(index, 'number', e.target.value)}
                        />
                        {errors[`precatorio_${index}_number`] && (
                          <span className="form-error">{errors[`precatorio_${index}_number`]}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Valor (R$) *</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="R$ 0,00"
                          value={precatorio.value}
                          onChange={(e) => handlePrecatorioChange(index, 'value', e.target.value)}
                        />
                        {errors[`precatorio_${index}_value`] && (
                          <span className="form-error">{errors[`precatorio_${index}_value`]}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Data do Precatório *</label>
                        <input
                          type="date"
                          className="form-input"
                          value={precatorio.date}
                          onChange={(e) => handlePrecatorioChange(index, 'date', e.target.value)}
                        />
                        {errors[`precatorio_${index}_date`] && (
                          <span className="form-error">{errors[`precatorio_${index}_date`]}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Estado *</label>
                        <select
                          className="form-select"
                          value={precatorio.state}
                          onChange={(e) => handlePrecatorioChange(index, 'state', e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {states.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                        {errors[`precatorio_${index}_state`] && (
                          <span className="form-error">{errors[`precatorio_${index}_state`]}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addPrecatorio}
                >
                  + Adicionar Outro Precatório
                </button>
              </div>
              
              {/* Form Actions */}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/')} disabled={isLoading}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'Enviando...' : 'Enviar Cadastro'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 MICA Capital. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Registration;
